/**
 * GoalEngine
 *
 * Strategic control panel for any entity.
 * Admin-only editable. Displays the main goal, growth cycle and group rules.
 * Reads/writes via useStructuredGrowth — no direct Firestore calls here.
 */

import React, { useState, useCallback, memo } from 'react';
import {
  GoalIcon,
  GrowthIcon,
  SettingsIcon,
  AddIcon,
  DeleteIcon,
  CheckIcon,
  CloseIcon,
  EditIcon,
  MilestoneIcon,
  ICON_STYLES,
} from '../../components/icons';
import { useStructuredGrowth } from './useStructuredGrowth';
import type { StructuredGrowthEntityType, GrowthCycleType, GroupRule } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GoalEngineProps {
  entityId: string;
  entityType: StructuredGrowthEntityType;
  isAdmin: boolean;
  userId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CYCLE_LABELS: Record<GrowthCycleType, string> = {
  open: 'Open (geen vaste cyclus)',
  '90days': '90-daagse sprint',
  '6months': 'Halfjaarlijkse cyclus',
};

function cycleColor(type: GrowthCycleType): string {
  return type === '90days'
    ? 'bg-blue-50 text-blue-700 border-blue-100'
    : type === '6months'
    ? 'bg-purple-50 text-purple-700 border-purple-100'
    : 'bg-gray-50 text-gray-600 border-gray-100';
}

// ---------------------------------------------------------------------------
// Sub-component: Rule item
// ---------------------------------------------------------------------------

const RuleItem = memo(function RuleItem({
  rule,
  isAdmin,
  onDelete,
}: {
  rule: GroupRule;
  isAdmin: boolean;
  onDelete: (id: string) => void;
}) {
  return (
    <li className="flex items-start gap-2 group py-1.5">
      <MilestoneIcon size={14} weight="regular" className="text-gray-400 mt-0.5 shrink-0" />
      <span className="flex-1 text-sm text-gray-700 leading-snug">{rule.text}</span>
      {isAdmin && (
        <button
          onClick={() => onDelete(rule.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center rounded hover:bg-red-50 text-gray-300 hover:text-red-500"
          aria-label="Verwijder regel"
        >
          <CloseIcon size={12} weight="bold" />
        </button>
      )}
    </li>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const GoalEngine: React.FC<GoalEngineProps> = ({
  entityId,
  entityType,
  isAdmin,
  userId,
}) => {
  const { settings, loading, updateSettings } = useStructuredGrowth(entityId, entityType);

  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState('');
  const [newRule, setNewRule] = useState('');
  const [saving, setSaving] = useState(false);

  const handleGoalEdit = useCallback(() => {
    setGoalDraft(settings?.mainGoal ?? '');
    setEditingGoal(true);
  }, [settings?.mainGoal]);

  const handleGoalSave = useCallback(async () => {
    if (!goalDraft.trim()) return;
    setSaving(true);
    try {
      await updateSettings({ mainGoal: goalDraft.trim() }, userId);
      setEditingGoal(false);
    } finally {
      setSaving(false);
    }
  }, [goalDraft, updateSettings, userId]);

  const handleCycleChange = useCallback(
    async (cycle: GrowthCycleType) => {
      await updateSettings({ cycleType: cycle }, userId);
    },
    [updateSettings, userId],
  );

  const handleAddRule = useCallback(async () => {
    if (!newRule.trim()) return;
    const existingRules = settings?.groupRules ?? [];
    const updated: GroupRule[] = [
      ...existingRules,
      { id: `rule-${Date.now()}`, text: newRule.trim() },
    ];
    await updateSettings({ groupRules: updated }, userId);
    setNewRule('');
  }, [newRule, settings?.groupRules, updateSettings, userId]);

  const handleDeleteRule = useCallback(
    async (ruleId: string) => {
      const updated = (settings?.groupRules ?? []).filter((r) => r.id !== ruleId);
      await updateSettings({ groupRules: updated }, userId);
    },
    [settings?.groupRules, updateSettings, userId],
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-4 bg-gray-100 rounded w-1/2" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Main Goal ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <GoalIcon size={16} weight="regular" className="text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">Hoofddoel</span>
          </div>
          {isAdmin && !editingGoal && (
            <button
              onClick={handleGoalEdit}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
            >
              <EditIcon size={12} weight="regular" />
              <span>Bewerk</span>
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Wat wil deze groep samen bereiken? Het hoofddoel geeft richting aan elke sessie.
        </p>

        {editingGoal ? (
          <div className="space-y-2">
            <textarea
              value={goalDraft}
              onChange={(e) => setGoalDraft(e.target.value)}
              placeholder="Beschrijf het hoofddoel van deze groep..."
              rows={3}
              className="w-full text-sm text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleGoalSave}
                disabled={saving || !goalDraft.trim()}
                className="h-8 px-3 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              >
                <CheckIcon size={12} weight="bold" />
                <span>{saving ? 'Opslaan...' : 'Opslaan'}</span>
              </button>
              <button
                onClick={() => setEditingGoal(false)}
                className="h-8 px-3 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Annuleer
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-700 leading-relaxed">
            {settings?.mainGoal || (
              <span className="text-gray-400 italic">Nog geen hoofddoel ingesteld.</span>
            )}
          </p>
        )}
      </div>

      {/* ── Growth Cycle ───────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-1">
          <GrowthIcon size={16} weight="regular" className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Groeicyclus</span>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Over welke periode werkt de groep? Kies "90 dagen" voor een sprint met een duidelijk eindpunt.
        </p>
        {isAdmin ? (
          /* Admin: alle opties klikbaar */
          <div className="flex flex-wrap gap-2">
            {(Object.keys(CYCLE_LABELS) as GrowthCycleType[]).map((cycle) => {
              const active = (settings?.cycleType ?? 'open') === cycle;
              return (
                <button
                  key={cycle}
                  onClick={() => handleCycleChange(cycle)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                    active
                      ? cycleColor(cycle) + ' ring-1 ring-current ring-offset-0'
                      : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-200'
                  }`}
                >
                  {CYCLE_LABELS[cycle]}
                </button>
              );
            })}
          </div>
        ) : (
          /* Lid: alleen de actieve cyclus als badge */
          <span className={`inline-block text-xs font-medium px-3 py-1.5 rounded-lg border ${cycleColor(settings?.cycleType ?? 'open')}`}>
            {CYCLE_LABELS[settings?.cycleType ?? 'open']}
          </span>
        )}
      </div>

      {/* ── Group Rules ────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-1">
          <SettingsIcon size={16} weight="regular" className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Groepsregels</span>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Afspraken die de groep met elkaar maakt — over aanwezigheid, vertrouwelijkheid of feedback geven.
        </p>

        {(settings?.groupRules ?? []).length === 0 ? (
          <p className="text-xs text-gray-400 italic mb-3">Nog geen regels toegevoegd.</p>
        ) : (
          <ul className="space-y-0 mb-3 divide-y divide-gray-50">
            {(settings?.groupRules ?? []).map((rule) => (
              <RuleItem
                key={rule.id}
                rule={rule}
                isAdmin={isAdmin}
                onDelete={handleDeleteRule}
              />
            ))}
          </ul>
        )}

        {isAdmin && (
          <div className="flex items-center gap-2 mt-2">
            <input
              type="text"
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddRule()}
              placeholder="Nieuwe regel toevoegen..."
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 h-8 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
            <button
              onClick={handleAddRule}
              disabled={!newRule.trim()}
              className="h-8 w-8 flex items-center justify-center bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              aria-label="Voeg regel toe"
            >
              <AddIcon size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(GoalEngine);
